import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Volume2, 
  VolumeX, 
  SkipBack, 
  SkipForward,
  Rewind,
  FastForward,
  Pause,
  Play,
  Minimize2,
  Maximize2,
  Clock
} from 'lucide-react';
import type { PodcastWithRelations } from '../../../hooks/usePodcasts';

interface AudioPlayerProps {
  podcast: PodcastWithRelations;
  isPlaying: boolean;
  controls: {
    currentTime: number;
    duration: number;
    volume: number;
    handleTimeUpdate: (value: number) => void;
    handleVolumeChange: (value: number) => void;
    toggleMute: () => void;
  };
  onPlayPause: (podcast: PodcastWithRelations) => void;
}

const formatTime = (time: number) => {
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

export const AudioPlayer: React.FC<AudioPlayerProps> = ({
  podcast,
  isPlaying,
  controls,
  onPlayPause
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const {
    currentTime,
    duration,
    volume,
    handleTimeUpdate,
    handleVolumeChange,
    toggleMute
  } = controls;

  const handlePlaybackRateChange = () => {
    const rates = [0.5, 0.75, 1, 1.25, 1.5, 2];
    const currentIndex = rates.indexOf(playbackRate);
    const nextIndex = (currentIndex + 1) % rates.length;
    setPlaybackRate(rates[nextIndex]);
    // Aqui você pode adicionar a lógica para alterar a velocidade real do áudio
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        className={`fixed bottom-0 left-0 right-0 bg-card/95 backdrop-blur-lg border-t border-border z-50 transition-all duration-300 ${
          isExpanded ? 'h-96' : 'h-24'
        }`}
      >
        {/* Barra de Progresso Superior */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-secondary overflow-hidden">
          <motion.div
            className="h-full bg-primary"
            style={{ width: `${(currentTime / duration) * 100}%` }}
            initial={false}
            animate={{ width: `${(currentTime / duration) * 100}%` }}
            transition={{ duration: 0.1 }}
          />
        </div>

        <div className="container mx-auto px-4 h-full flex flex-col">
          {/* Player Compacto */}
          <div className="h-24 flex items-center gap-4">
            {/* Imagem do Podcast */}
            <motion.div
              className="w-16 h-16 rounded-lg overflow-hidden"
              whileHover={{ scale: 1.05 }}
            >
              <img
                src={podcast.image_url}
                alt={podcast.title}
                className="w-full h-full object-cover"
              />
            </motion.div>

            {/* Informações do Podcast */}
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold truncate">{podcast.title}</h3>
              <p className="text-sm text-gray-400 truncate">{podcast.author}</p>
            </div>

            {/* Controlos Principais */}
            <div className="flex items-center gap-4">
              <button
                onClick={() => handleTimeUpdate(Math.max(0, currentTime - 10))}
                className="p-2 hover:bg-secondary rounded-full transition-colors"
              >
                <Rewind className="w-5 h-5" />
              </button>

              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => onPlayPause(podcast)}
                className="p-4 bg-primary rounded-full text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                {isPlaying ? (
                  <Pause className="w-6 h-6" />
                ) : (
                  <Play className="w-6 h-6" />
                )}
              </motion.button>

              <button
                onClick={() => handleTimeUpdate(Math.min(duration, currentTime + 10))}
                className="p-2 hover:bg-secondary rounded-full transition-colors"
              >
                <FastForward className="w-5 h-5" />
              </button>
            </div>

            {/* Controlos Secundários */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <button
                  onClick={toggleMute}
                  className="p-2 hover:bg-secondary rounded-full transition-colors"
                >
                  {volume === 0 ? (
                    <VolumeX className="w-5 h-5" />
                  ) : (
                    <Volume2 className="w-5 h-5" />
                  )}
                </button>
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.1}
                  value={volume}
                  onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                  className="w-24 h-1 bg-secondary rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary"
                />
              </div>

              <button
                onClick={handlePlaybackRateChange}
                className="flex items-center gap-1 px-2 py-1 rounded-full hover:bg-secondary transition-colors text-sm"
              >
                <Clock className="w-4 h-4" />
                {playbackRate}x
              </button>

              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="p-2 hover:bg-secondary rounded-full transition-colors"
              >
                {isExpanded ? (
                  <Minimize2 className="w-5 h-5" />
                ) : (
                  <Maximize2 className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>

          {/* Player Expandido */}
          {isExpanded && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex-1 py-4"
            >
              <div className="flex gap-8 h-full">
                {/* Lado Esquerdo - Imagem Grande */}
                <div className="w-64 h-64">
                  <motion.div
                    className="w-full h-full rounded-lg overflow-hidden"
                    whileHover={{ scale: 1.02 }}
                  >
                    <img
                      src={podcast.image_url}
                      alt={podcast.title}
                      className="w-full h-full object-cover"
                    />
                  </motion.div>
                </div>

                {/* Lado Direito - Informações Detalhadas */}
                <div className="flex-1">
                  <h2 className="text-2xl font-bold mb-2">{podcast.title}</h2>
                  <p className="text-gray-400 mb-4">{podcast.description}</p>
                  
                  {/* Tempo e Duração */}
                  <div className="flex items-center gap-4 text-sm text-gray-400 mb-4">
                    <span>{formatTime(currentTime)}</span>
                    <div className="flex-1 h-1 bg-secondary rounded-full">
                      <input
                        type="range"
                        min={0}
                        max={duration || 100}
                        value={currentTime}
                        onChange={(e) => handleTimeUpdate(parseFloat(e.target.value))}
                        className="w-full h-full appearance-none bg-transparent cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary"
                      />
                    </div>
                    <span>{formatTime(duration)}</span>
                  </div>

                  {/* Controlos Avançados */}
                  <div className="flex items-center justify-center gap-6">
                    <button
                      onClick={() => handleTimeUpdate(Math.max(0, currentTime - 30))}
                      className="flex flex-col items-center gap-1"
                    >
                      <div className="p-3 hover:bg-secondary rounded-full transition-colors">
                        <SkipBack className="w-6 h-6" />
                      </div>
                      <span className="text-xs text-gray-400">30s</span>
                    </button>

                    <button
                      onClick={() => handleTimeUpdate(Math.max(0, currentTime - 10))}
                      className="flex flex-col items-center gap-1"
                    >
                      <div className="p-3 hover:bg-secondary rounded-full transition-colors">
                        <Rewind className="w-6 h-6" />
                      </div>
                      <span className="text-xs text-gray-400">10s</span>
                    </button>

                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => onPlayPause(podcast)}
                      className="flex flex-col items-center gap-1"
                    >
                      <div className="p-6 bg-primary rounded-full text-primary-foreground hover:bg-primary/90 transition-colors">
                        {isPlaying ? (
                          <Pause className="w-8 h-8" />
                        ) : (
                          <Play className="w-8 h-8" />
                        )}
                      </div>
                    </motion.button>

                    <button
                      onClick={() => handleTimeUpdate(Math.min(duration, currentTime + 10))}
                      className="flex flex-col items-center gap-1"
                    >
                      <div className="p-3 hover:bg-secondary rounded-full transition-colors">
                        <FastForward className="w-6 h-6" />
                      </div>
                      <span className="text-xs text-gray-400">10s</span>
                    </button>

                    <button
                      onClick={() => handleTimeUpdate(Math.min(duration, currentTime + 30))}
                      className="flex flex-col items-center gap-1"
                    >
                      <div className="p-3 hover:bg-secondary rounded-full transition-colors">
                        <SkipForward className="w-6 h-6" />
                      </div>
                      <span className="text-xs text-gray-400">30s</span>
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};